/**
 * Board transform functions for Chaos Chess board events.
 *
 * All functions are pure — they take a GameState and return a new GameState.
 * No DOM, no side effects.
 *
 * ## Outer ring rotation (CW / CCW)
 * The "outer ring" is the perimeter of the board. On 8×8 it is the standard
 * 28-square perimeter. On 14×14 it is the perimeter of the full 14×14 grid
 * with the 9-square cut corners excluded (32 squares).
 *
 * A CW rotation applies the 90° CW coordinate transformation:
 *   8×8:   (col, row) → (7  − row, col)
 *   14×14: (col, row) → (13 − row, col)
 * This moves each ring piece exactly n/4 steps clockwise (7 for 8×8, 8 for 14×14).
 *
 * ## Center rotation (always 180°)
 * The center 4×4 block rotates 180°:
 *   8×8:   center cols 2–5, rows 2–5 → (col, row) → (7  − col, 7  − row)
 *   14×14: center cols 5–8, rows 5–8 → (col, row) → (13 − col, 13 − row)
 */

import type { GameState, Facing, Pickup } from './types'

// ── Facing rotation lookup tables ─────────────────────────────────────────────

const FACING_CW: Record<Facing, Facing> = {
  UP: 'RIGHT', RIGHT: 'DOWN', DOWN: 'LEFT', LEFT: 'UP',
}
const FACING_CCW: Record<Facing, Facing> = {
  UP: 'LEFT', LEFT: 'DOWN', DOWN: 'RIGHT', RIGHT: 'UP',
}
const FACING_180: Record<Facing, Facing> = {
  UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT',
}

// ── Ring / center definitions ─────────────────────────────────────────────────

/**
 * Returns the outer ring squares in clockwise order.
 *
 * 8×8  — 28 squares: full perimeter (row 0, col 7, row 7, col 0).
 * 14×14 — 32 squares: perimeter of the original 14×14 grid with the
 *   three-square cut sections on each corner skipped.
 *   Top row cols 3–10, right col rows 3–10, bottom row cols 10–3,
 *   left col rows 10–3 (each arm has 8 valid squares = 32 total).
 */
export function getOuterRing(boardSize: 8 | 14): string[] {
  if (boardSize === 8) {
    const ring: string[] = []
    for (let col = 0; col <= 7; col++) ring.push(`${col},0`)        // top row →
    for (let row = 1; row <= 7; row++) ring.push(`7,${row}`)         // right col ↓
    for (let col = 6; col >= 0; col--) ring.push(`${col},7`)         // bottom row ←
    for (let row = 6; row >= 1; row--) ring.push(`0,${row}`)         // left col ↑
    return ring
  } else {
    const ring: string[] = []
    for (let col = 3; col <= 10; col++) ring.push(`${col},0`)        // top row →
    for (let row = 3; row <= 10; row++) ring.push(`13,${row}`)       // right col ↓
    for (let col = 10; col >= 3; col--) ring.push(`${col},13`)       // bottom row ←
    for (let row = 10; row >= 3; row--) ring.push(`0,${row}`)        // left col ↑
    return ring
  }
}

/**
 * Returns the 16 squares of the center 4×4 block.
 * 8×8:   cols 2–5, rows 2–5.
 * 14×14: cols 5–8, rows 5–8.
 */
export function getCenterSquares(boardSize: 8 | 14): string[] {
  const min = boardSize === 8 ? 2 : 5
  const squares: string[] = []
  for (let row = min; row <= min + 3; row++)
    for (let col = min; col <= min + 3; col++)
      squares.push(`${col},${row}`)
  return squares
}

// ── Core transform engine ─────────────────────────────────────────────────────

/**
 * Apply a position mapping to all pieces that are currently on squares in
 * `squareKeys`. Pieces outside the set are not touched.
 *
 * Rules (matching CLAUDE.md invariants):
 * - Transform is atomic: all source pieces are cleared before any destination
 *   is written, so a piece can move into another piece's vacated square.
 * - If two pieces would land on the same destination, the piece with the
 *   lower player index wins; the other is removed (captured by transform).
 * - If a destination key is not present in state.squares (invalid / cut
 *   corner), the piece is silently removed from the board.
 * - Pawn facing is updated by facingMap in the same call as its position.
 */
function applySquareTransform(
  state: GameState,
  squareKeys: Iterable<string>,
  positionFn: (col: number, row: number) => [number, number],
  facingMap: Record<Facing, Facing>,
): GameState {
  // 1. Gather all pieces on the affected squares and compute their destinations.
  const moves: Array<{
    fromKey: string
    toKey: string
    playerIndex: number
  }> = []

  for (const fromKey of squareKeys) {
    const sq = state.squares[fromKey]
    if (!sq?.piece) continue
    const [toCol, toRow] = positionFn(sq.col, sq.row)
    moves.push({ fromKey, toKey: `${toCol},${toRow}`, playerIndex: sq.piece.owner })
  }

  // Sort by player index so the lower (higher-priority) index always wins ties.
  moves.sort((a, b) => a.playerIndex - b.playerIndex)

  // 2. Clone the squares map and clear all source squares (atomic step).
  let newSquares = { ...state.squares }
  for (const { fromKey } of moves) {
    newSquares = { ...newSquares, [fromKey]: { ...newSquares[fromKey], piece: null } }
  }

  // 3. Write pieces to their destinations.
  const placed = new Set<string>()
  for (const { fromKey, toKey } of moves) {
    const piece = state.squares[fromKey]?.piece
    if (!piece) continue
    // Destination must exist in the board (invalid / cut corner → piece removed)
    if (!(toKey in newSquares)) continue
    // First piece to claim this square wins (earlier in sorted order = lower player index)
    if (!placed.has(toKey)) {
      placed.add(toKey)
      newSquares = {
        ...newSquares,
        [toKey]: {
          ...newSquares[toKey],
          piece: { ...piece, facing: facingMap[piece.facing] },
        },
      }
    }
    // else: conflict — this piece is captured by the higher-priority piece
  }

  return { ...state, squares: newSquares }
}

// ── Diagonal strip helpers ────────────────────────────────────────────────────

/**
 * Returns the ordered square keys on a single NE-SW diagonal (col+row = k),
 * including only valid board squares. Ordered SW→NE (col ascending).
 */
function nesw_diagonal(k: number, boardSize: 8 | 14, validKeys: Set<string>): string[] {
  const out: string[] = []
  for (let col = 0; col < boardSize; col++) {
    const row = k - col
    if (row < 0 || row >= boardSize) continue
    const key = `${col},${row}`
    if (validKeys.has(key)) out.push(key)
  }
  return out
}

/**
 * Returns the ordered square keys on a single NW-SE diagonal (col-row = k),
 * including only valid board squares. Ordered NW→SE (col ascending).
 */
function nwse_diagonal(k: number, boardSize: 8 | 14, validKeys: Set<string>): string[] {
  const out: string[] = []
  for (let col = 0; col < boardSize; col++) {
    const row = col - k
    if (row < 0 || row >= boardSize) continue
    const key = `${col},${row}`
    if (validKeys.has(key)) out.push(key)
  }
  return out
}

/**
 * Returns all squares in the diagonal strips affected by a diagonal shift event.
 *
 * NE/SW events use NE-SW diagonals (col+row = K), selecting every other K (K%2==0).
 * NW/SE events use NW-SE diagonals (col-row = K), selecting every other K (K%2==0).
 *
 * Exported so the UI can highlight these squares.
 */
export function getDiagonalStripSquares(
  boardSize: 8 | 14,
  family: 'NESW' | 'NWSE',
  validKeys: Set<string>,
): string[] {
  const out: string[] = []
  const n = boardSize - 1
  if (family === 'NESW') {
    for (let k = 0; k <= 2 * n; k += 2) {
      out.push(...nesw_diagonal(k, boardSize, validKeys))
    }
  } else {
    for (let k = -n; k <= n; k += 2) {
      out.push(...nwse_diagonal(k, boardSize, validKeys))
    }
  }
  return out
}

// ── Pickup remapping ──────────────────────────────────────────────────────────

/**
 * Remap pickups that sit on `squareKeys` using the same position function
 * as the piece transform. Pickups landing on invalid/cut-corner squares
 * are removed. If two pickups collide the last one written wins (rare).
 */
function remapPickups(
  state: GameState,
  squareKeys: Iterable<string>,
  positionFn: (col: number, row: number) => [number, number],
): Record<string, Pickup> {
  const newPickups: Record<string, Pickup> = { ...state.pickups }
  const pending: Array<{ pickup: Pickup; toKey: string }> = []

  for (const fromKey of squareKeys) {
    const pickup = newPickups[fromKey]
    if (!pickup) continue
    delete newPickups[fromKey]
    const sq = state.squares[fromKey]
    const [toCol, toRow] = positionFn(sq.col, sq.row)
    const toKey = `${toCol},${toRow}`
    if (toKey in state.squares) pending.push({ pickup, toKey })
  }

  for (const { pickup, toKey } of pending) {
    newPickups[toKey] = pickup
  }

  return newPickups
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Rotate all pieces on the outer ring 90° clockwise or counter-clockwise.
 *
 * Each ring piece moves n/4 positions around the ring (7 on 8×8, 8 on 14×14).
 * Pawn facing rotates by the same angle as the board section.
 *
 * CW formula  (8×8): (col, row) → (7  − row, col)
 * CCW formula (8×8): (col, row) → (row, 7  − col)
 * (Replace 7 with 13 for 14×14.)
 */
export function applyOuterRingRotation(state: GameState, direction: 'CW' | 'CCW'): GameState {
  const n = state.boardSize - 1  // 7 for 8×8, 13 for 14×14
  const ring = getOuterRing(state.boardSize)

  let positionFn: (col: number, row: number) => [number, number]
  let facingMap: Record<Facing, Facing>

  if (direction === 'CW') {
    positionFn = (col, row) => [n - row, col]
    facingMap = FACING_CW
  } else {
    positionFn = (col, row) => [row, n - col]
    facingMap = FACING_CCW
  }

  const afterPieces = applySquareTransform(state, ring, positionFn, facingMap)
  return { ...afterPieces, pickups: remapPickups(state, ring, positionFn) }
}

/**
 * Slide all pieces on every-other diagonal strip one step along their lane,
 * wrapping around within the same diagonal lane.
 *
 * NE/SW events affect NE-SW diagonals (col+row = K, K even).
 * NW/SE events affect NW-SE diagonals (col-row = K, K even).
 *
 * NE/SE = move toward higher col index in the ordered lane (wrap from end to start).
 * SW/NW = move toward lower col index (wrap from start to end).
 *
 * No facing change — diagonal slides don't rotate pieces.
 * No eliminations — pieces always wrap to the other end of their lane.
 */
export function applyDiagonalShift(state: GameState, direction: 'NE' | 'SW' | 'NW' | 'SE'): GameState {
  const validKeys = new Set(Object.keys(state.squares))
  const isNESW = direction === 'NE' || direction === 'SW'
  const family = isNESW ? 'NESW' : 'NWSE'
  const toHigherCol = direction === 'NE' || direction === 'SE'

  // Build a map: squareKey → ordered lane (array of keys in that diagonal)
  const laneOf = new Map<string, string[]>()
  const n = state.boardSize - 1

  if (family === 'NESW') {
    for (let k = 0; k <= 2 * n; k += 2) {
      const lane = nesw_diagonal(k, state.boardSize, validKeys)
      for (const key of lane) laneOf.set(key, lane)
    }
  } else {
    for (let k = -n; k <= n; k += 2) {
      const lane = nwse_diagonal(k, state.boardSize, validKeys)
      for (const key of lane) laneOf.set(key, lane)
    }
  }

  // Compute destination for each piece on an affected square
  const moves: Array<{ fromKey: string; toKey: string; playerIndex: number }> = []

  for (const [fromKey, lane] of laneOf) {
    const sq = state.squares[fromKey]
    if (!sq?.piece) continue
    const idx = lane.indexOf(fromKey)
    const toIdx = toHigherCol
      ? (idx + 1) % lane.length
      : (idx - 1 + lane.length) % lane.length
    moves.push({ fromKey, toKey: lane[toIdx], playerIndex: sq.piece.owner })
  }

  // Apply atomically: lower player index wins ties (same as applySquareTransform)
  moves.sort((a, b) => a.playerIndex - b.playerIndex)

  let newSquares = { ...state.squares }
  for (const { fromKey } of moves) {
    newSquares = { ...newSquares, [fromKey]: { ...newSquares[fromKey], piece: null } }
  }

  const placed = new Set<string>()
  for (const { fromKey, toKey } of moves) {
    const piece = state.squares[fromKey]?.piece
    if (!piece) continue
    if (!placed.has(toKey)) {
      placed.add(toKey)
      newSquares = { ...newSquares, [toKey]: { ...newSquares[toKey], piece: { ...piece } } }
    }
  }

  // Remap pickups using the same lane wrapping
  const newPickups: Record<string, Pickup> = { ...state.pickups }
  const pickupPending: Array<{ pickup: Pickup; toKey: string }> = []
  for (const [fromKey, lane] of laneOf) {
    const pickup = newPickups[fromKey]
    if (!pickup) continue
    delete newPickups[fromKey]
    const idx = lane.indexOf(fromKey)
    const toIdx = toHigherCol
      ? (idx + 1) % lane.length
      : (idx - 1 + lane.length) % lane.length
    pickupPending.push({ pickup, toKey: lane[toIdx] })
  }
  for (const { pickup, toKey } of pickupPending) {
    newPickups[toKey] = pickup
  }

  return { ...state, squares: newSquares, pickups: newPickups }
}

/**
 * Rotate all pieces in the center 4×4 block 180° around the block's center.
 *
 * Formula (8×8):   (col, row) → (7  − col, 7  − row)
 * Formula (14×14): (col, row) → (13 − col, 13 − row)
 *
 * Pawn facing rotates 180°: UP↔DOWN, LEFT↔RIGHT.
 */
export function applyCenterRotation(state: GameState): GameState {
  const n = state.boardSize - 1
  const center = getCenterSquares(state.boardSize)
  const positionFn = (col: number, row: number): [number, number] => [n - col, n - row]
  const afterPieces = applySquareTransform(state, center, positionFn, FACING_180)
  return { ...afterPieces, pickups: remapPickups(state, center, positionFn) }
}
